import { useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CEFR } from "@/lib/constants";
import LanguageSelect from "@/components/LanguageSelect";
import SerbianScriptSelect from "@/components/SerbianScriptSelect";
import { pairAllowed, isSerbian } from "@/lib/languages";
import { useI18n } from "@/context/I18nContext";
import { takeReturn } from "@/lib/returnPath";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [native, setNative] = useState("en");
  const [target, setTarget] = useState("es");
  const [level, setLevel] = useState("A1");
  const [script, setScript] = useState("cyrillic");
  const [loading, setLoading] = useState(false);
  const dest = useRef(null);  // consumed return path survives the onboarding_complete re-render

  const pickNative = (v) => { setNative(v); if (!pairAllowed(v, target)) setTarget("en"); };
  const pickTarget = (v) => { setTarget(v); if (!pairAllowed(native, v)) setNative("en"); };
  const showScript = isSerbian(target) || isSerbian(native);

  const finish = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/onboard", {
        native_lang: native, target_lang: target, level,
        target_script: showScript ? script : undefined,
      });
      dest.current = takeReturn() || "/dashboard";
      setUser(res.data);
      toast.success(t("onboarding.allSet"));
      navigate(dest.current, { replace: true });
    } catch (e) {
      if (e?.response?.status === 409) {
        dest.current = takeReturn() || "/dashboard";
        return navigate(dest.current, { replace: true });
      }
      toast.error(t("errors.couldNotSave"));
    } finally { setLoading(false); }
  };

  // 8.3.7 — onboarding is a one-way transition; completed accounts never see it again.
  if (user?.onboarding_complete) return <Navigate to={dest.current || "/dashboard"} replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="nb-card max-w-lg w-full p-6 md:p-8">
        <div className="nb-tag bg-lavender mb-3">{t("onboarding.welcome")}</div>
        <h1 className="font-cabinet font-extrabold text-3xl">{t("onboarding.title")}</h1>
        <p className="text-gray-600 text-sm mt-1">{t("onboarding.subtitle")}</p>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">{t("onboarding.iSpeak")}</label>
            <LanguageSelect role="native" value={native} onChange={pickNative} partner={target} testid="onboard-native" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">{t("onboarding.imLearning")}</label>
            <LanguageSelect role="target" value={target} onChange={pickTarget} partner={native} testid="onboard-target" />
          </div>
        </div>
        {showScript && <SerbianScriptSelect value={script} onChange={setScript} testid="onboard-serbian-script" />}
        <label className="text-xs font-bold uppercase tracking-widest mt-4 block">{t("onboarding.level")}</label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {CEFR.map((c) => (
            <button key={c.level} onClick={() => setLevel(c.level)}
              className={`text-start border-2 border-ink rounded-xl p-2 transition-all ${level === c.level ? "bg-mint shadow-hard-sm -translate-y-0.5" : "bg-white"}`}>
              <div className="font-cabinet font-bold text-sm">{c.level}</div>
              <div className="text-[10px] leading-tight text-gray-600">{t(`cefr.${c.level}`)}</div>
            </button>
          ))}
        </div>
        <button onClick={finish} disabled={loading} className="nb-btn-primary w-full mt-6 py-3" data-testid="onboard-finish">
          {loading ? t("common.loading") : t("onboarding.finish")}
        </button>
      </motion.div>
    </div>
  );
}
